import { useGlobalState, useLocalPurchasedOrder } from '@libs/client'
import Footer from '@ui/components/shared/Footer'
import Header from '@ui/components/shared/Header'
import HowToFindOrderModal from '@ui/components/shared/HowToFindOrderModal'
import { iconPath, getLocalObject } from '@ui/helpers'
import { NextPage } from 'next'
import { useEffect, useRef, useState } from 'react'
import getConfig from 'next/config'
import { useNotiContext } from '@ui/components/shared/Notification'
import { useMounted, useRedirect } from '@ui/hooks'
import css from 'styled-jsx/css'
import { validateContact } from '@ui/validators'
import { ShopHelpLocalKey, useOrderService } from '@ui/services'
import ServeByShopHelp from '@ui/components/shared/ShopHelp/ServeByShopHelp'
// import getLogRocket from '@ui/analytics/logRocket'
import { LocalContact } from '@ui/modules'
import Loading from '@ui/components/shared/Loading'
import { LoginResponse } from '@ui/types/interfaces'
import { useAuthService } from '@ui/services/auth/auth.service'

const TrackingsSearch: NextPage = () => {
  const redirect = useRedirect()
  const [orderDetail, setOrderDetail] = useGlobalState<Types.OrderShopHelp>(
    ShopHelpLocalKey.ORDER_DETAIL
  )
  const [contactForm, setContactForm] = useGlobalState<any>(ShopHelpLocalKey.CONTACT_FORM)
  const [localPurchasedOrder] = useLocalPurchasedOrder()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingFull, setIsLoadingFull] = useState(false)
  const [openHowToFind, setOpenHowToFind] = useState<boolean>(false)
  const [error, setError] = useState<{ email: string; order: string }>({
    email: '',
    order: ''
  })
  const AUTH_LOGIN = 'AUTH_LOGIN'

  const emailRef = useRef<HTMLInputElement>(null)
  const orderRef = useRef<HTMLInputElement>(null)

  const { detailByInfoAuth } = useOrderService(getConfig().publicRuntimeConfig.shophelpBasePathApi)
  const { login } = useAuthService(getConfig().publicRuntimeConfig.shophelpBasePathApi)

  const { notiDispatch } = useNotiContext()

  const defaultEmail =
    contactForm?.email || orderDetail?.customer_info?.email || localPurchasedOrder?.shipping?.email
  const defaultOrder =
    contactForm?.order_name || contactForm?.order || orderDetail?.name || localPurchasedOrder?.code

  function toForgotOrder() {
    redirect(`/contact/forgot`, {
      routerOptions: { scroll: false },
      query: { ref: 'order-tracking' }
    })
  }

  async function onSubmit() {
    setIsLoading(true)
    try {
      const formValue = {
        email: emailRef.current?.value,
        order: orderRef.current?.value
      }
      await validateContact(formValue)
      setError({ email: '', order: '' })
      const result = await onSearch(formValue)
      if (result) {
        setContactForm({
          ...contactForm,
          ...formValue
        })
        redirect('/trackings/result', {
          query: { ...formValue }
        })
      } else {
        notiDispatch({
          payload: {
            content: "Sorry! We couldn't find what you were looking for",
            type: 'is-danger',
            timeout: 10
          },
          type: 'REMOVE_ALL_AND_ADD'
        })
        setError({ order: 'true', email: 'true' })
      }
    } catch (err: any) {
      if (err?.orderError) {
        setError(err?.orderError)
        return
      }
    } finally {
      setIsLoading(false)
    }
  }

  async function onSearch(data: Types.LoginModel): Promise<boolean> {
    return await fetchOrder(data).then(
      async (res: any) => {
        return !!res
      },
      () => {
        return false
      }
    )
  }

  async function fetchOrder(body: Types.LoginModel) {
    try {
      const localAuth = getLocalObject(AUTH_LOGIN)
      let result = true
      if (
        !localAuth ||
        !localAuth.access_token ||
        !localAuth.email ||
        localAuth.email != body.email
      ) {
        result = await login(body).then((data: LoginResponse) => {
          if (data && (data.email || data.phone) && data.access_token) {
            return true
          }
          return false
        })
      }
      if (result) {
        return await detailByInfoAuth(body).then((res: Types.OrderShopHelp) => {
          setOrderDetail(res)
          return res
        })
      }
    } catch (err: any) {
      console.log(err)
      setIsLoadingFull(false)
    }
  }

  // useEffect(() => {
  //   getLogRocket().logRocket?.init()
  // }, [])

  async function onLoginViaLocalStorage(email: string, order: string) {
    try {
      const formValue = {
        email: email,
        order: order
      }
      setContactForm({
        ...contactForm,
        ...formValue
      })
      const result = await onSearch(formValue)
      if (result) {
        redirect('/trackings/result', {
          query: { ...formValue }
        })
      }
    } catch (err: any) {
      console.log(err)
      setIsLoadingFull(false)
    } finally {
    }
    return
  }

  useEffect(() => {
    setIsLoadingFull(true)
    ;(async () => {
      const LocalContactForm = getLocalObject(LocalContact.contactKey)
      if (LocalContactForm) {
        if (LocalContactForm.email && (LocalContactForm.order_name || LocalContactForm.order)) {
          onLoginViaLocalStorage(
            LocalContactForm.email,
            LocalContactForm.order_name || LocalContactForm.order
          )
        }
      } else {
        setIsLoadingFull(false)
      }
    })()
  }, [])

  const isMounted = useMounted()

  return isMounted ? (
    <>
      <style jsx>{pageStyle}</style>
      {isLoadingFull && <Loading isFullWidth />}
      <Header />
      <div className="tracking-page">
        <div className="wrapper">
          <section className="container pr-4-desktop pl-4-desktop section section--medium">
            <h3 className="tracking__title">
              Order Tracking <ServeByShopHelp className="is-hidden-mobile ml-4" type="desktop" />
            </h3>
            <form
              className="search-order__form"
              onSubmit={(e) => {
                e.preventDefault()
                onSubmit().then()
              }}
            >
              <div className="columns">
                <div className="column is-10 is-offset-1">
                  <div className="field">
                    {/*eslint-disable-next-line jsx-a11y/label-has-associated-control*/}
                    <label className="label search-order__form__label">Email</label>
                    <div className="control">
                      <input
                        id="ord_tracking_email"
                        ref={emailRef}
                        type="text"
                        placeholder="email@example.com"
                        className={`input${error.email ? ' is-danger' : ''}`}
                        defaultValue={defaultEmail}
                        required
                      />
                    </div>
                    <p className="help is-danger">
                      {error.email && error.email !== 'true' ? error.email : ''}
                    </p>
                  </div>

                  <div className="field">
                    {/*eslint-disable-next-line jsx-a11y/label-has-associated-control*/}
                    <label className="label search-order__form__label">
                      Order Number (#123-45)
                    </label>
                    <div className="control has-icons-left mb-4">
                      <input
                        id="ord_tracking_num"
                        type="text"
                        placeholder="12-345"
                        ref={orderRef}
                        className={`input${error.order ? ' is-danger' : ''}`}
                        defaultValue={defaultOrder}
                        required
                      />
                      <span className="icon is-left" style={{ height: 56 }}>
                        #
                      </span>
                      <p className="help is-danger">
                        {error.order && error.order !== 'true' ? error.order : ''}
                      </p>
                    </div>
                  </div>

                  <div className="field">
                    <div className="control">
                      <a
                        className="is-link find-ord-order-tracking"
                        onClick={() => setOpenHowToFind(true)}
                      >
                        How to find your order
                      </a>
                    </div>
                  </div>

                  <div className="field">
                    <p className="control">
                      <button
                        id="ord_tracking_btn"
                        className={`button is-primary button--cta is-fullwidth-mobile${
                          isLoading ? ' is-loading' : ''
                        }`}
                        type="submit"
                      >
                        TRACK IT NOW
                      </button>
                    </p>
                  </div>
                  <ServeByShopHelp className="is-hidden-tablet" type="mobile" />
                  <div className="field field__notice">
                    <div className="notice__icon">
                      <img alt="Double quote icon" src={iconPath('double_quotes.svg')} />
                    </div>
                    <div className="notice__text">
                      Check your email for detailed tracking information and updates as well
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </section>
        </div>

        <HowToFindOrderModal
          open={openHowToFind}
          setOpen={setOpenHowToFind}
          toForgotOrder={toForgotOrder}
        />
      </div>
      <Footer />
    </>
  ) : null
}

const pageStyle = css.global`
  .tracking-page {
    a.is-link {
      color: #2d9cdb !important;
    }
    .search-order__form {
      max-width: 800px;
      margin: auto;
      margin-bottom: 2.5rem;

      &__error {
        margin-top: 0;
        margin-bottom: 0.75rem;

        ul {
          padding-left: 1rem;
        }
      }

      .field {
        margin-bottom: 2.5rem;
        position: relative;
      }

      &__label {
        font-weight: 500;
      }

      .input:focus,
      .input:active {
        border-color: inherit;
        box-shadow: none;
      }

      button[type='submit'] {
        min-width: 200px;
        font-weight: 500;

        &:disabled {
          background-color: #f8f8f8;
          color: rgba(0, 0, 0, 0.5);
          border-color: rgba(0, 0, 0, 0.2);
        }
      }

      a.is-link {
        color: #2d9cdb;

        &:hover {
          color: #2d9cdb;
          text-decoration: underline;
        }
      }

      .field__notice {
        margin-top: 3rem;
        display: flex;
        align-items: center;

        .notice__icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background-color: rgba(255, 67, 67, 0.2);
          margin-right: 0.75rem;
          position: relative;

          img {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
        }

        .notice__text {
          font-size: 0.925rem;
          line-height: 150%;
          color: #828282;
          max-width: 400px;
        }
      }
    }

    .tracking__title {
      display: flex;
      margin-bottom: 5rem;
      font-weight: 600;
      font-size: 2rem;
      text-align: center;
      align-items: baseline;
      justify-content: center;
    }

    .tracking__footer {
      background-color: var(--primary);
      color: #fff;
      text-align: center;
      padding: 1.5rem 0;
    }
  }

  .tracking-wrapper {
    padding: 0 0.75rem;
  }

  @media screen and (max-width: 575px) {
    .field__notice {
      flex-direction: column;

      .notice__text {
        margin-top: 1rem;
        text-align: center;
      }
    }

    .search-order__form .field a.is-link {
      text-align: center;
      display: block;
    }

    .tracking-page {
      max-width: 100vw;
      overflow: hidden;
    }
  }
`

export default TrackingsSearch
